import React, { Suspense, lazy } from 'react';
import "./App.css";
import "./styles/safe-area.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import LandingPage from "./components/LandingPage";
import { HelpPage, PrivacyPage, TermsPage } from "./components/pages/InfoPages";
import EmployeeLayout from "./components/layout/EmployeeLayout";
import { Toaster } from "./components/ui/toaster";
import { TooltipProvider } from "./components/ui/tooltip";
import BrowserRecommendationBanner from "./components/ui/BrowserRecommendationBanner";
import PageLoader from "./components/ui/PageLoader";
import SkipLink from "./components/ui/skip-link";

import { AuthProvider } from './context/AuthContext';
import { FeatureFlagsProvider } from './context/FeatureFlagsContext';
import { RateLimitProvider } from './context/RateLimitContext';
import { ThemeProvider } from './context/ThemeProvider';
import { ResponsiveProvider } from './context/ResponsiveContext';
import { HolidayProvider } from './context/HolidayContext';
import AuthCallback from './components/auth/AuthCallback';
import RateLimitedLegacyRedirect from './components/auth/RateLimitedLegacyRedirect';
import ProtectedRoute from './components/auth/ProtectedRoute';
import RoleProtectedRoute from './components/auth/RoleProtectedRoute';
import {
  MY_SKILL_MATRIX_ROUTE_ROLES,
  RESOURCE_FINDER_ROUTE_ROLES,
} from './utils/rolePriority';
import RoleBasedRedirect from './components/auth/RoleBasedRedirect';
import { FEATURE_AI_SURVEY_UI_ENABLED } from './config/features';
import { WalkthroughProvider } from './features/walkthrough';
import { Toaster as SonnerToaster } from './components/ui/sonner';

const ManageProjectsPage = lazy(() => import('./components/pages/ManageProjectsPage'));
const ManageClientsPage = lazy(() => import('./components/pages/ManageClientsPage'));
const ManageAdminsPage = lazy(() => import('./components/pages/ManageAdminsPage'));
const ManageProjectManagersPage = lazy(() => import('./components/pages/ManageProjectManagersPage'));
const ManageReportingManagersPage = lazy(() => import('./components/pages/ManageReportingManagersPage'));
const OptionalTimesheetsPage = lazy(() => import('./components/pages/OptionalTimesheetsPage'));
const ManageConfigurationPage = lazy(() => import('./components/pages/ManageConfigurationPage'));
const ManageHolidaysPage = lazy(() => import('./components/pages/ManageHolidaysPage'));
const AddClientPage = lazy(() => import('./components/pages/AddClientPage'));
const ReviewTimesheetsPage = lazy(() => import('./components/pages/ReviewTimesheetsPage'));
const TimesheetLogsPage = lazy(() => import('./components/pages/TimesheetLogsPage'));
const AdminDashboardPage = lazy(() => import('./components/pages/AdminDashboardPage'));
const TimesheetsByStatusPage = lazy(() => import('./components/pages/TimesheetsByStatusPage'));
const ProjectManagerDashboardPage = lazy(() => import('./components/pages/ProjectManagerDashboardPage'));
const NotificationsPage = lazy(() => import('./components/pages/NotificationsPage'));
const TimeAnalysisReportPage = lazy(() => import('./components/pages/TimeAnalysisReportPage'));
const ProjectHoursViewPage = lazy(() => import('./components/pages/ProjectHoursViewPage'));
const AllEmployeeReportPage = lazy(() => import('./components/pages/AllEmployeeReportPage'));
const ApprovedTimesheetsPage = lazy(() => import('./components/pages/ApprovedTimesheetsPage'));
const AIAgentSurveyPage = lazy(() => import('./components/pages/AIAgentSurveyPage'));
const SurveyResponsesPage = lazy(() => import('./components/pages/SurveyResponsesPage'));
const SurveyResponseDetailPage = lazy(() => import('./components/pages/SurveyResponseDetailPage'));
const MySkillMatrixPage = lazy(() => import('./components/pages/MySkillMatrixPage'));
const ManageSkillsPage = lazy(() => import('./components/pages/ManageSkillsPage'));
const ResourceFinderPage = lazy(() => import('./components/pages/ResourceFinderPage'));
const BenchManagementPage = lazy(() => import('./components/pages/BenchManagementPage'));
const BenchAnalysisPage = lazy(() => import('./components/pages/BenchAnalysisPage'));
const MyAuditLogPage = lazy(() => import('./components/audit-log/MyAuditLogPage'));
const AdminAuditLogPage = lazy(() => import('./components/audit-log/AdminAuditLogPage'));
const ProfilePage = lazy(() => import('./components/profile/ProfilePage'));
const PublicResumePage = lazy(() => import('./components/profile/PublicResumePage'));
const EmployeeProfilePage = lazy(() => import('./components/profile/EmployeeProfilePage'));
const TeamResumesPage = lazy(() => import('./components/profile/TeamResumesPage'));

function LazyPage({ children }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

function WalkthroughGate({ children }) {
  return <WalkthroughProvider>{children}</WalkthroughProvider>;
}

function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <RateLimitProvider>
          <ResponsiveProvider>
          <AuthProvider>
            <FeatureFlagsProvider>
              <HolidayProvider>
              <WalkthroughGate>
              <TooltipProvider delayDuration={300}>
              <div className="App">
                <SkipLink />
                <BrowserRecommendationBanner />
                <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/auth/rate-limited" element={<RateLimitedLegacyRedirect />} />

            <Route
              path="/r/:token"
              element={
                <LazyPage>
                  <PublicResumePage />
                </LazyPage>
              }
            />

            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <RoleBasedRedirect />
                </ProtectedRoute>
              }
            />

            <Route
              path="/employee"
              element={
                <RoleProtectedRoute allowedRoles={['employee']}>
                  <EmployeeLayout />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/employee/ai-agent-survey"
              element={
                <RoleProtectedRoute allowedRoles={['employee', 'projectManager']}>
                  {FEATURE_AI_SURVEY_UI_ENABLED ? (
                    <EmployeeLayout>
                      <LazyPage><AIAgentSurveyPage /></LazyPage>
                    </EmployeeLayout>
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )}
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/admin/dashboard"
              element={
                <RoleProtectedRoute allowedRoles={['admin']}>
                  <EmployeeLayout>
                    <LazyPage><AdminDashboardPage /></LazyPage>
                  </EmployeeLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/admin/projects"
              element={
                <RoleProtectedRoute allowedRoles={['adminOnly']}>
                  <EmployeeLayout>
                    <LazyPage><ManageProjectsPage /></LazyPage>
                  </EmployeeLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/admin/clients"
              element={
                <RoleProtectedRoute allowedRoles={['adminOnly']}>
                  <EmployeeLayout>
                    <LazyPage><ManageClientsPage /></LazyPage>
                  </EmployeeLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/admin/admins"
              element={
                <RoleProtectedRoute allowedRoles={['adminOnly']}>
                  <EmployeeLayout>
                    <LazyPage><ManageAdminsPage /></LazyPage>
                  </EmployeeLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/admin/project-managers"
              element={
                <RoleProtectedRoute allowedRoles={['adminOnly']}>
                  <EmployeeLayout>
                    <LazyPage><ManageProjectManagersPage /></LazyPage>
                  </EmployeeLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/admin/reporting-managers"
              element={
                <RoleProtectedRoute allowedRoles={['admin']}>
                  <EmployeeLayout>
                    <LazyPage><ManageReportingManagersPage /></LazyPage>
                  </EmployeeLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/admin/optional-timesheets"
              element={
                <RoleProtectedRoute allowedRoles={['adminOnly']}>
                  <EmployeeLayout>
                    <LazyPage><OptionalTimesheetsPage /></LazyPage>
                  </EmployeeLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/admin/manage-csg"
              element={<Navigate to="/admin/dashboard" replace />}
            />
            <Route
              path="/admin/configuration"
              element={
                <RoleProtectedRoute allowedRoles={['adminOnly']}>
                  <EmployeeLayout>
                    <LazyPage><ManageConfigurationPage /></LazyPage>
                  </EmployeeLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/admin/holidays"
              element={
                <RoleProtectedRoute allowedRoles={['adminOnly']}>
                  <EmployeeLayout>
                    <LazyPage><ManageHolidaysPage /></LazyPage>
                  </EmployeeLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/admin/notifications"
              element={
                <RoleProtectedRoute allowedRoles={['admin']}>
                  <EmployeeLayout>
                    <LazyPage><NotificationsPage /></LazyPage>
                  </EmployeeLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/admin/add-client"
              element={
                <RoleProtectedRoute allowedRoles={['adminOnly']}>
                  <EmployeeLayout>
                    <LazyPage><AddClientPage /></LazyPage>
                  </EmployeeLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/admin/timesheets/:status"
              element={
                <RoleProtectedRoute allowedRoles={['admin']}>
                  <EmployeeLayout>
                    <LazyPage><TimesheetsByStatusPage /></LazyPage>
                  </EmployeeLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/admin/approved-timesheets"
              element={
                <RoleProtectedRoute allowedRoles={['admin']}>
                  <EmployeeLayout>
                    <LazyPage><ApprovedTimesheetsPage /></LazyPage>
                  </EmployeeLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/pm/approved-timesheet"
              element={
                <RoleProtectedRoute allowedRoles={['projectManager']}>
                  <EmployeeLayout>
                    <LazyPage><ApprovedTimesheetsPage /></LazyPage>
                  </EmployeeLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/csg/approved-timesheets"
              element={
                <RoleProtectedRoute allowedRoles={['csg', 'admin', 'projectManager']}>
                  <EmployeeLayout>
                    <LazyPage><ApprovedTimesheetsPage /></LazyPage>
                  </EmployeeLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/csg/project-hours"
              element={
                <RoleProtectedRoute allowedRoles={['csg']}>
                  <EmployeeLayout>
                    <LazyPage><ProjectHoursViewPage /></LazyPage>
                  </EmployeeLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/csg/time-analysis"
              element={
                <RoleProtectedRoute allowedRoles={['csg']}>
                  <EmployeeLayout>
                    <LazyPage><TimeAnalysisReportPage /></LazyPage>
                  </EmployeeLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/admin/time-analysis"
              element={
                <RoleProtectedRoute allowedRoles={['admin']}>
                  <EmployeeLayout>
                    <LazyPage><TimeAnalysisReportPage /></LazyPage>
                  </EmployeeLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/admin/project-hours"
              element={
                <RoleProtectedRoute allowedRoles={['admin']}>
                  <EmployeeLayout>
                    <LazyPage><ProjectHoursViewPage /></LazyPage>
                  </EmployeeLayout>
                </RoleProtectedRoute>
              }
            />

            <Route
              path="/pm/dashboard"
              element={
                <RoleProtectedRoute allowedRoles={['projectManager']}>
                  <EmployeeLayout>
                    <LazyPage><ProjectManagerDashboardPage /></LazyPage>
                  </EmployeeLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/pm/timesheet"
              element={
                <RoleProtectedRoute allowedRoles={['projectManager', 'reportingManager', 'admin']}>
                  <EmployeeLayout />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/pm/review-timesheets"
              element={
                <RoleProtectedRoute allowedRoles={['projectManager', 'reportingManager']}>
                  <EmployeeLayout>
                    <LazyPage><ReviewTimesheetsPage /></LazyPage>
                  </EmployeeLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/pm/timesheet-logs"
              element={
                <RoleProtectedRoute allowedRoles={['projectManager', 'reportingManager']}>
                  <EmployeeLayout>
                    <LazyPage><TimesheetLogsPage /></LazyPage>
                  </EmployeeLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/pm/reviewed-timesheets"
              element={
                <RoleProtectedRoute allowedRoles={['projectManager']}>
                  <Navigate to="/pm/timesheet-logs" replace />
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/pm/time-analysis"
              element={
                <RoleProtectedRoute allowedRoles={['admin']}>
                  <EmployeeLayout>
                    <LazyPage><TimeAnalysisReportPage /></LazyPage>
                  </EmployeeLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/pm/project-hours"
              element={
                <RoleProtectedRoute allowedRoles={['admin']}>
                  <EmployeeLayout>
                    <LazyPage><ProjectHoursViewPage /></LazyPage>
                  </EmployeeLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/admin/employee-report"
              element={
                <RoleProtectedRoute allowedRoles={['admin']}>
                  <EmployeeLayout>
                    <LazyPage><AllEmployeeReportPage /></LazyPage>
                  </EmployeeLayout>
                </RoleProtectedRoute>
              }
            />

            <Route
              path="/skills/my-matrix"
              element={
                <RoleProtectedRoute allowedRoles={[...MY_SKILL_MATRIX_ROUTE_ROLES]}>
                  <EmployeeLayout>
                    <LazyPage><MySkillMatrixPage /></LazyPage>
                  </EmployeeLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/admin/skills/manage"
              element={
                <RoleProtectedRoute allowedRoles={['adminOnly']}>
                  <EmployeeLayout>
                    <LazyPage><ManageSkillsPage /></LazyPage>
                  </EmployeeLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/skills/resource-finder"
              element={
                <RoleProtectedRoute allowedRoles={[...RESOURCE_FINDER_ROUTE_ROLES]}>
                  <EmployeeLayout>
                    <LazyPage><ResourceFinderPage /></LazyPage>
                  </EmployeeLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/skills/bench-management"
              element={
                <RoleProtectedRoute allowedRoles={['admin']}>
                  <EmployeeLayout>
                    <LazyPage><BenchManagementPage /></LazyPage>
                  </EmployeeLayout>
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/skills/bench-analysis"
              element={
                <RoleProtectedRoute allowedRoles={['adminOnly']}>
                  <EmployeeLayout>
                    <LazyPage><BenchAnalysisPage /></LazyPage>
                  </EmployeeLayout>
                </RoleProtectedRoute>
              }
            />

            <Route
              path="/account/audit-log"
              element={
                <ProtectedRoute>
                  <EmployeeLayout>
                    <LazyPage><MyAuditLogPage /></LazyPage>
                  </EmployeeLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/account/profile"
              element={
                <ProtectedRoute>
                  <EmployeeLayout>
                    <LazyPage><ProfilePage /></LazyPage>
                  </EmployeeLayout>
                </ProtectedRoute>
              }
            />

            <Route
              path="/account/employee-profile/:userId"
              element={
                <RoleProtectedRoute allowedRoles={['admin', 'deliveryHead', 'projectManager', 'reportingManager']}>
                  <EmployeeLayout>
                    <LazyPage><EmployeeProfilePage /></LazyPage>
                  </EmployeeLayout>
                </RoleProtectedRoute>
              }
            />

            <Route
              path="/account/team-resumes/:userId?"
              element={
                <RoleProtectedRoute allowedRoles={['admin', 'deliveryHead', 'projectManager', 'reportingManager']}>
                  <EmployeeLayout>
                    <LazyPage><TeamResumesPage /></LazyPage>
                  </EmployeeLayout>
                </RoleProtectedRoute>
              }
            />

            <Route
              path="/admin/audit-log"
              element={
                <RoleProtectedRoute allowedRoles={['adminOnly']}>
                  <EmployeeLayout>
                    <LazyPage><AdminAuditLogPage /></LazyPage>
                  </EmployeeLayout>
                </RoleProtectedRoute>
              }
            />

            <Route
              path="/admin/survey-responses"
              element={
                <RoleProtectedRoute allowedRoles={['admin']}>
                  {FEATURE_AI_SURVEY_UI_ENABLED ? (
                    <EmployeeLayout>
                      <LazyPage><SurveyResponsesPage /></LazyPage>
                    </EmployeeLayout>
                  ) : (
                    <Navigate to="/admin/dashboard" replace />
                  )}
                </RoleProtectedRoute>
              }
            />
            <Route
              path="/admin/survey-response/:responseId"
              element={
                <RoleProtectedRoute allowedRoles={['admin']}>
                  {FEATURE_AI_SURVEY_UI_ENABLED ? (
                    <EmployeeLayout>
                      <LazyPage><SurveyResponseDetailPage /></LazyPage>
                    </EmployeeLayout>
                  ) : (
                    <Navigate to="/admin/dashboard" replace />
                  )}
                </RoleProtectedRoute>
              }
            />

            <Route path="*" element={<Navigate to="/" replace />} />

                </Routes>
                <Toaster />
                <SonnerToaster richColors closeButton position="top-right" />
              </div>
              </TooltipProvider>
              </WalkthroughGate>
              </HolidayProvider>
            </FeatureFlagsProvider>
          </AuthProvider>
          </ResponsiveProvider>
        </RateLimitProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
